import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import {
	Box,
	Button,
	Chip,
	FormControl,
	IconButton,
	InputAdornment,
	MenuItem,
	OutlinedInput,
	Select,
	Stack,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme
} from "@mui/material";
import ResponsiveButton from "components/ResponsiveButton";
import {
	DEFAULT_FLEET_STATUS_FILTER,
	FLEET_STATUS_FILTER_OPTIONS,
	FLEET_STATUS_LABELS,
	FleetStatusFilterOption,
	FleetVehicleStatus
} from "enums/FleetEnums";

interface FleetFilterProps {
	fleetStatus: FleetStatusFilterOption;
	onFleetStatusChange: (value: FleetStatusFilterOption) => void;
	searchFilter: string;
	onSearchFilterChange: (value: string) => void;
	onAddNew: () => void;
	onOpenFilterModal: () => void;
	onReset?: () => void;
	canWrite?: boolean;
}

export default function FleetFilter({
	fleetStatus,
	onFleetStatusChange,
	searchFilter,
	onSearchFilterChange,
	onAddNew,
	onOpenFilterModal,
	onReset,
	canWrite = false
}: FleetFilterProps) {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md"));

	const activeFilterChips = [
		fleetStatus !== DEFAULT_FLEET_STATUS_FILTER
			? {
					key: "status" as const,
					label: `Status: ${FLEET_STATUS_LABELS[fleetStatus as FleetVehicleStatus] ?? fleetStatus}`
				}
			: null,
		searchFilter ? { key: "search" as const, label: `Search: ${searchFilter}` } : null
	].filter((c): c is { key: "status" | "search"; label: string } => c !== null);

	if (isMobile) {
		return (
			<>
				<Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
					<OutlinedInput
						size="small"
						placeholder="Search fleet"
						value={searchFilter}
						onChange={(e) => onSearchFilterChange(e.target.value)}
						startAdornment={
							<InputAdornment position="start">
								<SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
							</InputAdornment>
						}
						endAdornment={
							searchFilter ? (
								<InputAdornment position="end">
									<IconButton size="small" onClick={() => onSearchFilterChange("")} edge="end">
										<ClearIcon fontSize="small" />
									</IconButton>
								</InputAdornment>
							) : undefined
						}
						sx={{ flexGrow: 1, backgroundColor: "background.paper" }}
					/>
					<Button
						size="small"
						onClick={onOpenFilterModal}
						startIcon={<FilterAltIcon fontSize="small" />}
						sx={{
							textTransform: "none",
							fontWeight: 700,
							color: "primary.main",
							whiteSpace: "nowrap"
						}}
					>
						Filter
					</Button>
				</Box>
				{canWrite && (
					<Box sx={{ display: "flex", mt: 2 }}>
						<ResponsiveButton
							variant="contained"
							onClick={onAddNew}
							startIcon={<AddIcon sx={{ fontSize: 2 }} />}
							sx={{
								textTransform: "none",
								fontWeight: 700,
								fontSize: { xs: "14px", md: "16px" },
								lineHeight: { xs: "16px", md: "24px" },
								px: { xs: "9px", md: "14px" },
								py: { xs: "5px", md: "7px" },
								boxShadow: "none",
								borderRadius: 1,
								width: "100%"
							}}
						>
							Add new vehicle
						</ResponsiveButton>
					</Box>
				)}
				{activeFilterChips.length > 0 && (
					<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 2 }}>
						{activeFilterChips.map((chip) => (
							<Chip
								key={chip.key}
								label={chip.label}
								size="small"
								onDelete={() => {
									if (chip.key === "status") {
										onFleetStatusChange(DEFAULT_FLEET_STATUS_FILTER);
									} else {
										onSearchFilterChange("");
									}
								}}
								sx={{
									border: "1px solid",
									borderColor: "secondary.light30",
									borderRadius: 1,
									backgroundColor: "secondary.light8",
									"& .MuiChip-deleteIcon": { color: "secondary.main" },
									"& .MuiChip-label": { typography: "text14" }
								}}
							/>
						))}
					</Box>
				)}
			</>
		);
	}

	return (
		<Stack
			direction={{ xs: "column", sm: "row" }}
			sx={{
				width: "100%",
				alignItems: { xs: "stretch", sm: "flex-end", md: "center" },
				justifyContent: { md: "flex-start" },
				flexWrap: { sm: "wrap", md: "nowrap" },
				gap: { xs: 1.5, sm: 2.5, md: 2 }
			}}
		>
			{/* Filter Section */}
			<Box
				sx={{
					display: "flex",
					alignItems: { xs: "stretch", md: "center" },
					flexDirection: { xs: "column", md: "row" },
					gap: { xs: 1, md: 2.5 },
					flexBasis: { sm: "calc(50% - 10px)", md: "auto" }
				}}
			>
				<Typography variant="text14Bold" sx={{ color: "text.primary", whiteSpace: "nowrap" }}>
					Status:
				</Typography>
				<FormControl
					size="small"
					sx={{ flexGrow: { xs: 1, md: "auto" }, minWidth: { xs: "auto", md: 150 } }}
				>
					<Select
						value={fleetStatus}
						onChange={(e) => onFleetStatusChange(e.target.value as FleetStatusFilterOption)}
						sx={{ backgroundColor: "background.paper" }}
					>
						{Object.values(FLEET_STATUS_FILTER_OPTIONS).map((status) => (
							<MenuItem key={status} value={status}>
								{FLEET_STATUS_LABELS[status as FleetVehicleStatus] ?? status}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				{(fleetStatus !== DEFAULT_FLEET_STATUS_FILTER || !!searchFilter) && (
					<Button
						size="small"
						startIcon={<RefreshIcon fontSize="small" sx={{ transform: "scaleX(-1)" }} />}
						onClick={onReset}
						sx={{ textTransform: "none", color: "primary.main", whiteSpace: "nowrap" }}
					>
						<Typography variant="text14Bold">Reset</Typography>
					</Button>
				)}
			</Box>

			{/* Search Section */}
			<Box
				sx={{
					ml: { md: "auto" },
					mb: { xs: 1.5, sm: 0 },
					flexBasis: { sm: "calc(50% - 10px)", md: "auto" }
				}}
			>
				<Tooltip title="Search by fleet number or tractor/trailer registration" placement="top">
					<OutlinedInput
						size="small"
						placeholder="Search fleet"
						value={searchFilter}
						onChange={(e) => onSearchFilterChange(e.target.value)}
						startAdornment={
							<InputAdornment position="start">
								<SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
							</InputAdornment>
						}
						endAdornment={
							searchFilter && (
								<InputAdornment position="end">
									<IconButton size="small" onClick={() => onSearchFilterChange("")} edge="end">
										<ClearIcon fontSize="small" />
									</IconButton>
								</InputAdornment>
							)
						}
						sx={{
							width: { xs: "100%", md: 220, lg: 220 },
							backgroundColor: "background.paper"
						}}
					/>
				</Tooltip>
			</Box>

			{/* Add Button Section */}
			{canWrite && (
				<ResponsiveButton
					variant="contained"
					color="primary"
					onClick={onAddNew}
					startIcon={<AddIcon sx={{ fontSize: 2 }} />}
					sx={{
						textTransform: "none",
						fontWeight: 700,
						fontSize: { xs: "14px", md: "16px" },
						lineHeight: { xs: "16px", md: "24px" },
						px: { xs: "9px", md: "14px" },
						py: { xs: "5px", md: "7px" },
						boxShadow: "none",
						borderRadius: 1,
						minWidth: "max-content",
						whiteSpace: "nowrap",
						width: { xs: "100%", sm: "auto" }
					}}
				>
					Add new vehicle
				</ResponsiveButton>
			)}
		</Stack>
	);
}
